import { useState } from "react";
import { Card, Head, ScrollX, State, useAsync } from "@authoxi/ui-shared";
import { directory, directoryAgent, directoryHost } from "../api";

/**
 * A page per host and per agent.
 *
 * Two page types under one section, sharing nothing. That is correct and the UI must not imply
 * otherwise: a scanned host is a URL somebody pointed the grader at; a registered agent is a
 * tenant-owned identity with a did. There is no link between them and inventing one would mean
 * claiming we know which agents run at a host, which we do not.
 */
export function DirectoryIndex() {
  const [offset, setOffset] = useState(0);
  const limit = 8;
  const { data, loading, error } = useAsync(() => directory(limit, offset), [offset]);

  return (
    <>
      <Head
        pill="Directory"
        title="A page for every host and every agent."
        lede="Indexable, linkable and machine-readable — every page here has a .json twin, because a UI that has to scrape HTML is a UI we made someone else's problem."
      />

      {loading && <State kind="loading" title="Loading the index…" />}
      {error && <State kind="error" title="Could not load the directory" detail={error} />}

      {data && data.hosts.length === 0 && (
        <State
          kind="empty"
          title="No hosts yet."
          detail="The directory lists endpoints somebody has run through the grader."
        />
      )}

      {data && data.hosts.length > 0 && (
        <>
          <Card pad>
            <ScrollX>
              <table className="table" data-testid="directory">
                <thead>
                  <tr>
                    <th>Host</th>
                    <th>Grade</th>
                    <th>Checks</th>
                    <th>Last checked</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.hosts.map((h) => (
                    <tr key={h.host}>
                      <td className="host">{h.host}</td>
                      <td>
                        <span className={`mini-grade g-${h.letter}`}>{h.letter}</span>
                      </td>
                      <td className="mono muted">
                        {h.checks_passed}/{h.checks_total}
                      </td>
                      <td className="sm muted">{h.scanned_at.slice(0, 10)}</td>
                      <td style={{ textAlign: "right" }}>
                        <a className="btn" href={`/tools/directory/${h.host}`}>
                          Open
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          </Card>

          <div className="row" style={{ marginTop: "var(--s4)" }}>
            <button
              className="btn"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              ← Previous
            </button>
            <button
              className="btn"
              disabled={!data.has_more}
              data-testid="directory-next"
              onClick={() => setOffset(offset + limit)}
            >
              Next →
            </button>
          </div>
        </>
      )}
    </>
  );
}

export function DirectoryHost({ host }: { host: string }) {
  const { data, loading, error } = useAsync(() => directoryHost(host), [host]);

  return (
    <>
      <a className="btn btn-quiet" href="/tools/directory" style={{ marginBottom: "var(--s4)" }}>
        ← Directory
      </a>

      {loading && <State kind="loading" title={`Loading ${host}…`} />}
      {error && (
        <State
          kind="empty"
          title="Nothing scanned for that host yet."
          detail="That is not a verdict — it means nobody has pointed the grader at it."
          action={
            <a className="btn btn-go" href="/tools/grader" style={{ marginTop: "var(--s4)" }}>
              Scan it now
            </a>
          }
        />
      )}

      {data && (
        <>
          <Head
            pill="Public record"
            title={data.host}
            lede="Everything on this page is already public at this host's own well-known paths. This is a readable rendering of it, not a source of truth — the agent's own origin is."
          />
          <Card pad={false}>
            <div className="grade">
              <div className={`grade-letter g-${data.letter}`}>{data.letter}</div>
              <div>
                <p className="label">Most recent grade</p>
                <h2 className="md" style={{ margin: "var(--s2) 0 0" }}>
                  {data.score} / 100
                </h2>
                <p className="sm muted" style={{ margin: "var(--s2) 0 0" }}>
                  Checked {data.scanned_at.slice(0, 10)}
                </p>
              </div>
            </div>
            <div className="checks">
              {data.checks.map((c) => (
                <div className="check" key={c.check_id}>
                  <span className={`mk ${c.passed ? "pass" : "fail"}`}>
                    {c.passed ? "PASS" : "FAIL"}
                  </span>
                  <div>
                    <strong>{c.title}</strong>
                    <p>{c.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
          <div className="row wrap" style={{ marginTop: "var(--s4)" }}>
            <button
              className="btn"
              onClick={() => void navigator.clipboard?.writeText(location.href)}
            >
              Copy link
            </button>
            <a className="btn" href={`/tools/whois?q=${encodeURIComponent(data.host)}`}>
              Look it up
            </a>
            <span className="sm muted">
              Machine-readable: <span className="mono">/directory/{data.host}.json</span>
            </span>
          </div>
        </>
      )}
    </>
  );
}

export function DirectoryAgent({ id }: { id: string }) {
  const { data, loading, error } = useAsync(() => directoryAgent(id), [id]);

  return (
    <>
      <a className="btn btn-quiet" href="/tools/directory" style={{ marginBottom: "var(--s4)" }}>
        ← Directory
      </a>

      {loading && <State kind="loading" title="Loading the agent…" />}
      {error && <State kind="empty" title="No such agent." detail="It may have been revoked." />}

      {data && (
        <>
          <Head pill={data.kind} title={data.name ?? data.agent_id} />
          <Card pad>
            <dl className="kv">
              <dt>agent id</dt>
              <dd className="mono">{data.agent_id}</dd>
              <dt>did</dt>
              <dd className="mono">{data.did}</dd>
              <dt>kind</dt>
              <dd>{data.kind}</dd>
              <dt>accountable human</dt>
              {/* THREE states, not two. An agent that names nobody has not denied having a
                  human behind it — it has not said — and a reader deciding whether to depend
                  on it is entitled to tell those apart. */}
              <dd>
                {data.principal_did ? (
                  <span className="mono">{data.principal_did}</span>
                ) : data.acts_for_human ? (
                  <span className="muted">claimed, but not named</span>
                ) : (
                  <span className="muted">none named — and it does not claim one</span>
                )}
              </dd>
              <dt>registered</dt>
              <dd>{data.registered}</dd>
            </dl>
          </Card>

          <Card pad className="">
            <p className="label">Check it without taking our word for it</p>
            <div className="stack" style={{ marginTop: "var(--s3)" }}>
              {Object.entries(data.verify).map(([k, url]) => (
                <div className="row" key={k}>
                  <span className="sm muted" style={{ minWidth: 140 }}>
                    {k.replace(/_/g, " ")}
                  </span>
                  <a className="mono sm" href={url} rel="external">
                    {url}
                  </a>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </>
  );
}
